import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { TextArea } from "@/components/TextArea/TextArea";

import { AiTestResponse } from "../../schema/hubspot";
import { AiConfigLabels, AiConfigModalCallbacks, AiConfigProspectOption } from "./aiConfig.types";
import {
	applyAiVariablesToPrompt,
	buildAiVariablesFromAllowedValues,
	buildAiVariablesFromProspect,
	getAiVariableDisplayLabel,
} from "./aiConfig.utils";

interface AiTestPanelProps {
	labels: AiConfigLabels;
	prompt: string;
	fallbackValue?: string;
	allowedValues?: string[];
	callbacks: AiConfigModalCallbacks;
}

const TEST_STEPS = (labels: AiConfigLabels) => [
	labels.loadingLeadData,
	labels.loadingResearch,
	labels.loadingAnalyze,
	labels.loadingReturn,
];

export const AiTestPanel: FunctionComponent<AiTestPanelProps> = ({
	labels,
	prompt,
	fallbackValue,
	allowedValues,
	callbacks,
}) => {
	const targetFieldVariables = useMemo(
		() => buildAiVariablesFromAllowedValues(allowedValues),
		[allowedValues]
	);
	const [prospects, setProspects] = useState<AiConfigProspectOption[]>([]);
	const [selectedProspect, setSelectedProspect] = useState<AiConfigProspectOption | null>(null);
	const [variables, setVariables] = useState<Record<string, string>>(targetFieldVariables);
	const [defaultVariables, setDefaultVariables] =
		useState<Record<string, string>>(targetFieldVariables);
	const [isEditingVariables, setIsEditingVariables] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [isTestLoading, setIsTestLoading] = useState(false);
	const [result, setResult] = useState<AiTestResponse | null>(null);
	const [error, setError] = useState<string>("");
	const searchTimeoutRef = useRef<number | null>(null);
	const latestSearchRequestRef = useRef(0);
	const searchProspectsCallbackRef = useRef(callbacks.onSearchProspects);
	const cancelSearchCallbackRef = useRef(callbacks.onCancelSearchProspects);
	const hasEditedVariables = JSON.stringify(variables) !== JSON.stringify(defaultVariables);
	const prospectItems: AutoCompleteItem[] = prospects.map((prospect) => ({
		id: prospect.id,
		title: prospect.title,
	}));
	const resolvedPromptPreview = useMemo(
		() => (Object.keys(variables).length ? applyAiVariablesToPrompt(prompt, variables) : prompt),
		[prompt, variables]
	);
	const canRun = Boolean(selectedProspect && prompt.trim());

	useEffect(() => {
		searchProspectsCallbackRef.current = callbacks.onSearchProspects;
		cancelSearchCallbackRef.current = callbacks.onCancelSearchProspects;
	}, [callbacks.onCancelSearchProspects, callbacks.onSearchProspects]);

	// When the target field's allowed values change (e.g. modal opened on a different field),
	// reseed the target-field slice of the variables while preserving any prospect-derived keys
	// the user may have already loaded or edited.
	useEffect(() => {
		const keepProspectSlice = (current: Record<string, string>) =>
			Object.entries(current).reduce<Record<string, string>>((acc, [key, value]) => {
				if (key.startsWith("prospect_")) {
					acc[key] = value;
				}

				return acc;
			}, {});

		setVariables((current) => ({ ...targetFieldVariables, ...keepProspectSlice(current) }));
		setDefaultVariables((current) => ({
			...targetFieldVariables,
			...keepProspectSlice(current),
		}));
	}, [targetFieldVariables]);

	const clearPendingSearchTimeout = () => {
		if (!searchTimeoutRef.current) {
			return;
		}

		window.clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = null;
	};

	const cancelInFlightSearch = () => {
		cancelSearchCallbackRef.current?.();
	};

	const resetSearchState = () => {
		setProspects([]);
		setIsSearchLoading(false);
	};

	useEffect(() => {
		return () => {
			clearPendingSearchTimeout();
			cancelInFlightSearch();
		};
	}, []);

	const searchProspects = (query: string) => {
		clearPendingSearchTimeout();
		cancelInFlightSearch();

		if (!searchProspectsCallbackRef.current || query.trim().length < 2) {
			resetSearchState();

			return;
		}

		const requestId = latestSearchRequestRef.current + 1;

		latestSearchRequestRef.current = requestId;
		setIsSearchLoading(true);
		searchTimeoutRef.current = window.setTimeout(async () => {
			searchTimeoutRef.current = null;

			try {
				const nextProspects = await searchProspectsCallbackRef.current?.(query);

				if (latestSearchRequestRef.current === requestId) {
					setProspects(nextProspects ?? []);
				}
			} catch {
				if (latestSearchRequestRef.current === requestId) {
					setProspects([]);
				}
			} finally {
				if (latestSearchRequestRef.current === requestId) {
					setIsSearchLoading(false);
				}
			}
		}, 600);
	};

	const buildLoadedVariables = (): Record<string, string> => {
		if (!selectedProspect) {
			return variables;
		}

		return {
			...targetFieldVariables,
			...buildAiVariablesFromProspect(selectedProspect.data),
		};
	};

	const loadVariables = () => {
		if (!selectedProspect) {
			return;
		}

		setError("");
		setResult(null);

		const nextVariables = buildLoadedVariables();

		setVariables(nextVariables);
		setDefaultVariables(nextVariables);
		setIsEditingVariables(false);
	};

	const runTest = async () => {
		if (!selectedProspect) {
			return;
		}

		// Auto-load prospect variables if the user hasn't done so yet; otherwise the
		// resolved prompt sent to the backend would still contain bare {{prospect_*}}
		// placeholders. Local copies are used because setState updates are async.
		const hasProspectVariables = Object.keys(variables).some((key) => key.startsWith("prospect_"));
		const effectiveVariables = hasProspectVariables ? variables : buildLoadedVariables();

		if (!hasProspectVariables) {
			setVariables(effectiveVariables);
			setDefaultVariables(effectiveVariables);
			setIsEditingVariables(false);
		}

		const effectivePrompt = Object.keys(effectiveVariables).length
			? applyAiVariablesToPrompt(prompt, effectiveVariables)
			: prompt;

		setIsTestLoading(true);
		setError("");
		try {
			const response = await callbacks.onRunTest(
				selectedProspect.id,
				effectivePrompt || prompt,
				fallbackValue
			);

			setResult(response);
		} catch {
			setError(labels.testError);
		} finally {
			setIsTestLoading(false);
		}
	};

	return (
		<div
			className="rounded-3xl border border-border-light bg-background p-5"
			data-testid="ai-test-panel"
		>
			<div className="mb-4 flex items-center justify-between gap-3">
				<h3 className="text-sm font-semibold text-text">{labels.testTitle}</h3>
				<span className="rounded-full border border-border-light bg-brand-white px-3 py-1 text-xs font-semibold text-text-meta">
					{labels.freeDuringSetup}
				</span>
			</div>

			<div className="mb-4">
				<div className="text-sm font-semibold text-text">{labels.testIntroTitle}</div>
				<div className="mt-1 text-xs leading-5 text-text-meta">{labels.testIntroHelp}</div>
			</div>

			<div className="space-y-4">
				<AutoComplete
					data={prospectItems}
					disableLocalFilter
					handleReset={() => {
						setSelectedProspect(null);
						setVariables(targetFieldVariables);
						setDefaultVariables(targetFieldVariables);
						setResult(null);
						clearPendingSearchTimeout();
						cancelInFlightSearch();
						resetSearchState();
					}}
					handleSearch={searchProspects}
					handleSelect={(item) => {
						const matched = item ? prospects.find((p) => p.id === Number(item.id)) : null;

						setSelectedProspect(matched ?? null);
						setVariables(targetFieldVariables);
						setDefaultVariables(targetFieldVariables);
						setResult(null);
						clearPendingSearchTimeout();
						cancelInFlightSearch();
						resetSearchState();
					}}
					isLoading={isSearchLoading}
					label={labels.prospectLabel}
					placeholder={labels.prospectPlaceholder}
					selectedItem={selectedProspect?.title ?? ""}
				/>

				{selectedProspect && (
					<div className="rounded-2xl border border-border-light bg-brand-white px-4 py-3">
						<div className="text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
							{labels.prospectLabel}
						</div>
						<div className="mt-1 text-sm font-semibold text-text">{selectedProspect.title}</div>
					</div>
				)}

				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="w-fit">
						<Button
							className="!w-auto"
							color={ButtonColor.ACTION_SECONDARY}
							disabled={!canRun}
							size={ButtonSize.S}
							title={labels.loadVariables}
							type="button"
							onClick={loadVariables}
						/>
					</div>
					{Object.keys(variables).length > 0 && (
						<div className="flex items-center gap-2">
							<Button
								className="!w-auto"
								color={ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.XS}
								title={labels.editData}
								type="button"
								onClick={() => setIsEditingVariables((current) => !current)}
							/>
							{hasEditedVariables && (
								<Button
									className="!w-auto"
									color={ButtonColor.ACTION_SECONDARY}
									size={ButtonSize.XS}
									title={labels.revertEdits}
									type="button"
									onClick={() => {
										setVariables(defaultVariables);
										setIsEditingVariables(false);
									}}
								/>
							)}
						</div>
					)}
				</div>

				<div className="rounded-2xl border border-border-light bg-brand-white p-4">
					<div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
						{labels.variablesPreviewTitle}
					</div>
					{Object.keys(variables).length ? (
						<div className="max-h-[320px] space-y-3 overflow-y-auto pr-1">
							{Object.entries(variables).map(([key, value]) => {
								const displayLabel = getAiVariableDisplayLabel(key);
								const isLongValue = value.length > 80 || value.includes("\n");

								return (
									<div
										key={key}
										className="rounded-2xl border border-border-light bg-background p-3"
									>
										<div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-meta">
											{key}
										</div>
										<div className="mb-2 text-sm font-semibold text-text">{displayLabel}</div>
										{isEditingVariables ? (
											isLongValue ? (
												<TextArea
													className="!min-h-[120px]"
													handleChange={(event) =>
														setVariables((current) => ({ ...current, [key]: event.target.value }))
													}
													name={`ai-variable-${key}`}
													showError={false}
													value={value}
												/>
											) : (
												<InputField
													classNameWrapper="!mb-0"
													handleChange={(event) =>
														setVariables((current) => ({ ...current, [key]: event.target.value }))
													}
													name={`ai-variable-${key}`}
													showError={false}
													value={value}
												/>
											)
										) : (
											<div className="rounded-xl border border-border-light bg-brand-white px-3 py-2 text-sm text-text">
												{value || "—"}
											</div>
										)}
									</div>
								);
							})}
						</div>
					) : (
						<div className="py-6 text-center text-xs text-text-meta">{labels.noVariables}</div>
					)}
				</div>

				<div>
					<Button
						className="!w-full"
						disabled={!canRun}
						isLoading={isTestLoading}
						size={ButtonSize.M}
						title={result ? labels.rerunTest : labels.runTest}
						type="button"
						onClick={runTest}
					/>
					<div className="mt-2 text-center text-xs text-text-meta">{labels.testRunHelp}</div>
				</div>

				{isTestLoading && (
					<div className="rounded-2xl border border-border-light bg-brand-white p-4">
						<div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
							{labels.progressLabel}
						</div>
						<div className="space-y-2">
							{TEST_STEPS(labels).map((step, index) => (
								<div
									key={step}
									className="flex items-center gap-3 rounded-xl border border-border-light bg-background px-3 py-2 text-sm text-text"
								>
									<span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-white text-xs font-semibold text-brand-light">
										{index + 1}
									</span>
									{step}
								</div>
							))}
						</div>
					</div>
				)}

				{error && <div className="text-xs text-status-error">{error}</div>}

				{result ? (
					<div
						className="rounded-2xl border border-border-light bg-brand-white p-4"
						data-testid="ai-test-result"
					>
						<div className="mb-4 flex items-center justify-between gap-3">
							<div className="text-sm font-semibold text-text">{labels.resultReadyTitle}</div>
							{result.durationMs && (
								<div className="text-xs font-semibold text-text-meta">
									{labels.durationLabel}: {result.durationMs}ms
								</div>
							)}
						</div>
						<div className="space-y-3">
							<div>
								<div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
									{labels.valueLabel}
								</div>
								<div className="rounded-xl bg-background px-3 py-2 text-sm font-semibold text-text">
									{result.value}
								</div>
							</div>
							{result.fallbackUsed && (
								<div className="text-xs font-semibold text-text-warning">{labels.fallbackUsed}</div>
							)}
							{result.reasoning && (
								<div>
									<div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
										{labels.reasoningLabel}
									</div>
									<div className="rounded-xl border border-border-light bg-background px-3 py-2 text-sm leading-6 text-text-meta">
										{result.reasoning}
									</div>
								</div>
							)}
							{!!result.sources?.length && (
								<div>
									<div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-text-meta">
										{labels.sourcesLabel}
									</div>
									<div className="flex flex-wrap gap-2">
										{result.sources.map((source) => (
											<span
												key={source}
												className="rounded-full border border-border-light bg-background px-3 py-1 text-xs font-semibold text-text-meta"
											>
												{source}
											</span>
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				) : (
					<div className="rounded-2xl border border-border-light bg-brand-white p-4 text-center text-xs text-text-meta">
						{labels.emptyState}
						<div className="mt-1">{labels.noCredits}</div>
					</div>
				)}

				{resolvedPromptPreview && (
					<details className="rounded-2xl border border-border-light bg-brand-white p-4 text-xs text-text-meta">
						<summary className="cursor-pointer font-semibold text-text">
							{labels.finalPrompt}
						</summary>
						<pre className="mt-3 whitespace-pre-wrap font-sans text-xs leading-5 text-text-meta">
							{resolvedPromptPreview}
						</pre>
					</details>
				)}
			</div>
		</div>
	);
};
